import { INITIAL_WIZARD_STATE, type WizardState } from './types'

// The onboarding wizard navigates the user straight into a build flow once they
// finish, so its in-memory state is lost. We persist a snapshot to localStorage
// so the destination pages' onboarding banners can personalise from it.

const ANSWERS_KEY = 'plivo:onboarding:answers'
const AGENTS_STRIP_KEY = 'plivo:onboarding:agents-strip-dismissed'
const SIP_ONBOARDING_KEY = 'plivo:onboarding:sip-dismissed'
const AUDIO_ONBOARDING_KEY = 'plivo:onboarding:audio-dismissed'
const MESSAGING_ONBOARDING_KEY = 'plivo:onboarding:messaging-dismissed'

// A persisted boolean flag ('1' in localStorage), tolerant of storage failures
// (private mode / quota) — the flag just won't persist across reloads.
const storageFlag = (key: string) => ({
  get: (): boolean => {
    try {
      return localStorage.getItem(key) === '1'
    } catch {
      return false
    }
  },
  set: () => {
    try {
      localStorage.setItem(key, '1')
    } catch {
      // No-op.
    }
  },
})

const agentsStrip = storageFlag(AGENTS_STRIP_KEY)
const sipOnboarding = storageFlag(SIP_ONBOARDING_KEY)
const audioOnboarding = storageFlag(AUDIO_ONBOARDING_KEY)
const messagingOnboarding = storageFlag(MESSAGING_ONBOARDING_KEY)

export const saveOnboardingAnswers = (answers: WizardState) => {
  try {
    localStorage.setItem(ANSWERS_KEY, JSON.stringify(answers))
  } catch {
    // Ignore storage failures — the banners just won't personalise.
  }
}

// Stored answers may predate fields added later (agentDescription arrived with
// the single-step wizard), so the parsed blob is normalised over
// INITIAL_WIZARD_STATE: every declared field is genuinely present, and the
// WizardState return type is honest rather than an assertion old data breaks.
export const getOnboardingAnswers = (): WizardState | null => {
  try {
    const raw = localStorage.getItem(ANSWERS_KEY)
    return raw ? { ...INITIAL_WIZARD_STATE, ...(JSON.parse(raw) as Partial<WizardState>) } : null
  } catch {
    return null
  }
}

// Whether the AI-Agents onboarding banner has been dismissed. Shown on first
// visit to the Agents page (typically after picking "Voice AI agents") until
// the user closes it or creates a first agent; the flag is write-once.
export const isAgentsStripDismissed = agentsStrip.get
export const dismissAgentsStrip = agentsStrip.set

// SIP Trunking onboarding banner — same dismiss-and-persist behavior.
export const isSipOnboardingDismissed = sipOnboarding.get
export const dismissSipOnboarding = sipOnboarding.set

// Audio Streaming onboarding banner — same dismiss-and-persist behavior.
export const isAudioOnboardingDismissed = audioOnboarding.get
export const dismissAudioOnboarding = audioOnboarding.set

// Messaging onboarding block (shown on the Compliance page, which is where a
// messaging signup lands) — same dismiss-and-persist behavior.
export const isMessagingOnboardingDismissed = messagingOnboarding.get
export const dismissMessagingOnboarding = messagingOnboarding.set
